import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { MagnifyingGlassIcon, ChevronRightIcon, ArrowPathIcon, ShieldCheckIcon, CheckCircleIcon, NoSymbolIcon, ExclamationTriangleIcon, XMarkIcon, BoltIcon, UsersIcon } from '@heroicons/react/24/outline';
import { getRiskBorderClass, getIPStatusTextClass, getIPStatusBorderClass, getIPStatusBgClass, getIPStatusLabel, ipStatusTone } from '../utils/riskColors';
import StatusCircle from './ui/StatusCircle';
import Pagination from './Pagination';
import CopyButton from './CopyButton';
import { isDemoMode, DEMO_UNAVAILABLE_TITLE } from '../utils/demoMode';
import { buildAdjacencyIndex, adjacencyFor, adjacencyBadgeText } from '../utils/ipAdjacency';
import NonInteractiveEvidencePanel from './caseIps/NonInteractiveEvidencePanel';
import NonInteractiveGuidance from './caseIps/NonInteractiveGuidance';
import IpPrevalenceUsers from './ui/IpPrevalenceUsers';
import { getIpPrevalenceUsers } from '../api/client';

// IP-enrichment refresh spends ProxyCheck quota and MERGEs the shared Delta
// table, so it stays blocked in Demo Mode — shown disabled rather than letting
// the read-only guard throw at a prominent demo surface.
const DEMO = isDemoMode();

// Status indicator colors for the clickable circle
// Circle tones now live in riskColors + ui/StatusCircle (v1.139.0) so the IP
// table, URL Clicks, the AAD Session IDs tab and the Attacker Window session
// list all render the identical control.

function getRiskLevelBadge(level) {
  // Handle both numeric (from runbook) and string risk levels
  const numLevel = typeof level === 'number' ? level : parseInt(level, 10);
  if (!isNaN(numLevel)) {
    if (numLevel >= 70) return 'bg-red-900/60 text-red-300 border border-red-700/50';
    if (numLevel >= 30) return 'bg-amber-900/60 text-amber-300 border border-amber-700/50';
    return 'bg-green-900/60 text-green-300 border border-green-700/50';
  }
  switch (String(level).toLowerCase()) {
    case 'high':
      return 'bg-red-900/60 text-red-300 border border-red-700/50';
    case 'medium':
      return 'bg-amber-900/60 text-amber-300 border border-amber-700/50';
    case 'low':
      return 'bg-green-900/60 text-green-300 border border-green-700/50';
    default:
      return 'bg-gray-700/60 text-gray-400 border border-gray-600/50';
  }
}

function getRiskLevelLabel(level) {
  const numLevel = typeof level === 'number' ? level : parseInt(level, 10);
  if (!isNaN(numLevel)) {
    if (numLevel >= 70) return 'High';
    if (numLevel >= 30) return 'Medium';
    return 'Low';
  }
  return String(level || 'Unknown');
}

function FlagBadge({ active, label, colorClass }) {
  if (!active) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-gray-600">
        --
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}
    >
      {label}
    </span>
  );
}

// Inline-editable freeform comment for a single IP (IP Addresses tab is the only edit
// surface). Click the cell to edit; Enter / blur saves, Escape cancels, empty clears.
// When not editable (no onSave), renders the comment as read-only text.
function CommentCell({ comment, onSave, editable }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment || '');
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(comment || ''); }, [comment, editing]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim().slice(0, 200);
    if (next !== (comment || '')) onSave(next);
  };
  const cancel = () => { setEditing(false); setDraft(comment || ''); };

  if (!editable) {
    return <span className="text-gray-300 text-xs">{comment || '—'}</span>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        maxLength={200}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        placeholder="Add a note…"
        className="w-full bg-gray-900 border border-gray-600 rounded px-1.5 py-0.5 text-xs text-gray-200 focus:outline-none focus:border-sky-500"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="text-left w-full text-xs group/cmt"
      title="Click to edit comment"
    >
      {comment
        ? <span className="text-gray-300 group-hover/cmt:text-sky-300 break-words">{comment}</span>
        : <span className="text-gray-600 italic group-hover/cmt:text-sky-400">+ note</span>}
    </button>
  );
}

function parseBrowserSimple(userAgent) {
  if (!userAgent) return 'Unknown';
  const ua = userAgent.toLowerCase();
  if (ua === 'bav2ropc') return 'Legacy Auth';
  if (ua.includes('teams/')) return 'Teams';
  if (ua.includes('outlook-ios') || ua.includes('outlook-android')) return 'Outlook Mobile';
  if (ua.includes('microsoft office')) return 'Office Desktop';
  if (ua.includes('edg/') || ua.includes('edge/')) return 'Edge';
  if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
  if (ua.includes('python') || ua.includes('powershell')) return 'Script/API';
  return 'Other';
}

function formatShortTimestamp(ts) {
  if (!ts) return '\u2014';
  try {
    const d = new Date(ts);
    return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  } catch {
    return ts;
  }
}

export default function IPAddressTable({
  ipData = [],
  ipClassifications = {},
  ipComments = {},
  onUpdateIPComment = null,
  tenantSafeIPs = [],
  onCycleIPStatus,
  isSaving = false,
  signInEvents = [],
  userBaseline = null,
  serviceIPLabelMap = {},
  isAdmin = false,
  onRefreshIpEnrichment = null,
  onRefreshIpEnrichmentBatch = null,
  onFetchRealUserEvidence = null,
  onSetIPSafe = null,
  onSetIPsSafe = null,
  initialEvidence = null,
  onEvidenceChange = null,
  caseId = null,
  onAddMaliciousToKnownList = null,
  // Badge (never hide) IPs whose only appearance is in message-trace events (v1.147.1).
  markEmailOnlyIPs = false,
  // Non-interactive sign-in activity (v1.147.0)
  onFetchNonInteractive = null,
  nonInteractive = null,
  caseStartDate = '',
  iocDate = '',
  firstAttackerActivity = '',
  caseDeviceNames = null,
  sessionIndex = null,
  activityInWindow = null,
  hasMaliciousClassification = false,
  // "Who else used this IP?" (v1.179.0). The tenant is REQUIRED by the endpoint and this
  // table has never received it — `caseId` alone is not enough, and the route's alertId is
  // decorative so the case id serves as it.
  tenantId = null,
}) {
  // Confirmation modal for promoting case-confirmed malicious IPs to the blocklist.
  const [showAddMalicious, setShowAddMalicious] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  // On-demand IP enrichment refresh state (admin-only). One IP at a time.
  const [refreshingIp, setRefreshingIp] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  // Batch enrichment state ("Enrich unenriched" button).
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  // Real-user evidence state ("Check real-user evidence" button). evidenceByIp:
  // lowercased IP -> { tier, tierLabel, deviceName, trustType, isCompliant, registered,
  // prtCount, appCount, days, interactiveSuccess, asn, firstSeen, lastSeen, reasons[] }.
  const [evidenceByIp, setEvidenceByIp] = useState({});
  const [evidenceRunning, setEvidenceRunning] = useState(false);
  const [evidenceResult, setEvidenceResult] = useState(null);
  // Real-user tier filter ('' = all | 'managed' | 'registered' | 'any').
  const [evidenceFilter, setEvidenceFilter] = useState('');
  // Non-interactive fetch state + view filter ('' = all | 'new' | 'seen').
  const [niRunning, setNiRunning] = useState(false);
  const [niResult, setNiResult] = useState(null);
  const [niFilter, setNiFilter] = useState('');
  // "Who else used this IP?" — ON DEMAND, one IP at a time, never auto-fetched (the
  // Alerts page's "List users" precedent). Each lookup is a cross-tenant Databricks query
  // over 30 days, so fetching for every row on expand would be a per-row query nobody
  // asked for. Keyed by lowercased IP so a result survives collapse/re-expand.
  const [ipUsersByIp, setIpUsersByIp] = useState({});
  const [ipUsersLoadingIp, setIpUsersLoadingIp] = useState(null);
  const [ipUsersErrByIp, setIpUsersErrByIp] = useState({});

  const loadIpUsers = useCallback(async (ip) => {
    if (!ip || !tenantId) return;
    const key = String(ip).toLowerCase();
    setIpUsersLoadingIp(ip);
    setIpUsersErrByIp((prev) => ({ ...prev, [key]: null }));
    try {
      // The route's alertId is decorative — the endpoint never looks an alert up — so the
      // case id serves as it. Authorization is entirely the SERVER's: it scopes
      // cross-tenant identities to the caller's readable set and returns counts only for
      // the rest, which is why this control is open to every portal user.
      const res = await getIpPrevalenceUsers(caseId || 'lookup', tenantId, ip);
      setIpUsersByIp((prev) => ({ ...prev, [key]: res }));
    } catch (err) {
      setIpUsersErrByIp((prev) => ({ ...prev, [key]: err.message || 'Lookup failed.' }));
    } finally {
      setIpUsersLoadingIp(null);
    }
  }, [caseId, tenantId]);

  // Seed evidence from the case's persisted snapshot (survives reload) once, when it arrives.
  useEffect(() => {
    if (initialEvidence && Object.keys(initialEvidence).length > 0) {
      setEvidenceByIp((prev) => (Object.keys(prev).length === 0 ? initialEvidence : prev));
    }
  }, [initialEvidence]);

  // Grade every IP in the table (so already-Safe ones also show their evidence).
  const evidenceCandidateIps = useMemo(() => ipData.map((ip) => ip.IPAddress), [ipData]);
  const hasEvidence = useMemo(() => Object.keys(evidenceByIp).length > 0, [evidenceByIp]);

  const doFetchEvidence = useCallback(async () => {
    if (!onFetchRealUserEvidence || evidenceCandidateIps.length === 0) return;
    setEvidenceRunning(true);
    setEvidenceResult(null);
    try {
      const resp = await onFetchRealUserEvidence(evidenceCandidateIps);
      const map = {};
      (resp?.results || []).forEach((r) => { map[String(r.ip || '').toLowerCase()] = r; });
      // Merge against current state, then update + notify the parent OUTSIDE the
      // state updater (a side effect inside an updater doesn't reliably run in prod).
      const merged = { ...evidenceByIp, ...map };
      setEvidenceByIp(merged);
      // Persist only tier 1/2 entries — tier-0 ("no device evidence") has no pill to
      // restore, and persisting every IP's verbose reasons blows the 64 KB Azure Table
      // Storage per-property limit (→ 400 on the whole case update).
      if (onEvidenceChange) {
        const persist = {};
        Object.entries(merged).forEach(([k, v]) => { if (v && v.tier > 0) persist[k] = v; });
        onEvidenceChange(persist);
      }
      setEvidenceResult({ tier1: resp?.summary?.tier1 ?? 0, tier2: resp?.summary?.tier2 ?? 0, tier0: resp?.summary?.tier0 ?? 0 });
    } catch (e) {
      setEvidenceResult({ error: e?.message || 'Evidence lookup failed.' });
    } finally {
      setEvidenceRunning(false);
    }
  }, [onFetchRealUserEvidence, evidenceCandidateIps, evidenceByIp, onEvidenceChange]);

  // IPs in the table with no enrichment shown (no provider, no country, and not a
  // labeled Microsoft service IP) — the set the batch button fetches.
  // Can a backfill still recover the missing failure rows? Only while the window's
  // earliest day is inside Microsoft's ~30-day sign-in retention. 28 days is deliberately
  // conservative: promising a backfill that silently cannot work costs the log team real
  // time (see Issues/2026-08-19_Test-NonInteractive-On-Second-Case-TODO.md).
  const niFailuresRecoverable = useMemo(() => {
    const from = nonInteractive?.window?.from || nonInteractive?.window?.startDate;
    if (!from) return false;
    const t = Date.parse(`${String(from).slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(t)) return false;
    return (Date.now() - t) / 86400000 <= 28;
  }, [nonInteractive]);

  // The failure probe reads WHOLE DAYS but, on a high-volume tenant, not every day —
  // it is bounded by a row budget because reading `$.status.errorCode` means reading
  // raw_event_json for the tenant's entire window (65s on the largest tenant before
  // v1.148.9, which blew the query's own timeout). Every figure it returns is therefore
  // "of the days sampled", and the copy below has to say so: printing "2 of 31 days"
  // when only 5 were read is a number the analyst cannot check. Snapshots persisted
  // before v1.148.9 carry no `daysProbed`, so fall back to the window length.
  const niFail = nonInteractive?.failureCoverage || null;
  const niFailDays = niFail?.daysProbed || niFail?.daysInWindow || 0;
  const niFailSampled = !!niFail?.sampled;

  const unenrichedIps = useMemo(
    () => ipData
      .filter((ip) => !ip.Provider && !ip.Country && !serviceIPLabelMap[ip.IPAddress])
      .map((ip) => ip.IPAddress),
    [ipData, serviceIPLabelMap]
  );

  const doBatchRefresh = useCallback(async () => {
    if (!onRefreshIpEnrichmentBatch || unenrichedIps.length === 0) return;
    setBatchRunning(true);
    setBatchResult(null);
    try {
      const resp = await onRefreshIpEnrichmentBatch(unenrichedIps);
      setBatchResult({
        fetched: resp?.fetched ?? 0,
        surfaced: resp?.surfaced ?? 0,
        failed: resp?.failed ?? 0,
      });
    } catch (e) {
      setBatchResult({ error: e?.message || 'Batch enrichment failed.' });
    } finally {
      setBatchRunning(false);
    }
  }, [onRefreshIpEnrichmentBatch, unenrichedIps]);

  // Drive the refresh; mirror the AlertsPage 409 force-confirm + error display.
  const doRefreshIp = useCallback(async (ip, force = false) => {
    if (!onRefreshIpEnrichment || !ip) return;
    setRefreshingIp(ip);
    setRefreshError(null);
    try {
      await onRefreshIpEnrichment(ip, force);
    } catch (e) {
      if (e?.status === 409 && !force) {
        const lastUpdated = e.body?.lastUpdated;
        const hours = lastUpdated
          ? Math.max(0, Math.round((Date.now() - new Date(lastUpdated).getTime()) / 3600000))
          : null;
        const msg = hours != null
          ? `IP enrichment was last refreshed ${hours}h ago. Re-run anyway?`
          : 'IP enrichment was refreshed in the last 24h. Re-run anyway?';
        if (window.confirm(msg)) {
          return doRefreshIp(ip, true);
        }
        return;
      }
      setRefreshError({ ip, msg: e?.message || 'Refresh failed.' });
    } finally {
      setRefreshingIp(null);
    }
  }, [onRefreshIpEnrichment]);
  const [sortColumn, setSortColumn] = useState('EventCount');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [columnFilters, setColumnFilters] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);

  // Run the on-demand aggregate against the non-interactive feed. The recovered IPs are
  // injected into `ipData` by the parent, so on success we switch the view to "new to
  // this case": those rows carry EventCount 0 (correctly — they have no events IN this
  // case) and would otherwise sink to the bottom under the default EventCount sort,
  // which would make the whole feature a hunt rather than a click.
  const doFetchNonInteractive = useCallback(async () => {
    if (!onFetchNonInteractive) return;
    setNiRunning(true);
    setNiResult(null);
    try {
      const resp = await onFetchNonInteractive();
      const known = new Set(ipData.map((r) => r.IPAddress));
      const found = (resp?.ips || []).length;
      const added = (resp?.ips || []).filter((r) => r?.ip && !known.has(r.ip)).length;
      setNiResult({
        found,
        added,
        devices: resp?.summary?.distinctDevices ?? 0,
        events: resp?.summary?.totalEvents ?? 0,
        capped: !!resp?.summary?.rowsCapped,
        coverage: resp?.coverage || null,
        failureCoverage: resp?.failureCoverage || null,
      });
      if (added > 0) { setNiFilter('new'); setCurrentPage(1); }
    } catch (e) {
      setNiResult({ error: e?.message || 'Non-interactive lookup failed.' });
    } finally {
      setNiRunning(false);
    }
  }, [onFetchNonInteractive, ipData]);


  const baselineIPSet = useMemo(() => new Set(userBaseline?.knownIPs || []), [userBaseline]);

  // `/24` + ASN adjacency to the analyst's OWN classifications (v1.147.0).
  //
  // Every other per-IP verdict evaluates an address in isolation; attacker infrastructure
  // clusters. On the reference case the two IPs the case could not see at all sit in the
  // same /24 — and on the same ASN — as two the analyst had already marked malicious.
  // ASN comes from the non-interactive feed's own autonomousSystemNumber, not enrichment,
  // so it still fires on IPs that have no enrichment record whatsoever.
  const asnByIp = useMemo(() => {
    const m = {};
    (nonInteractive?.ips || []).forEach((r) => { if (r?.ip && r.asn) m[r.ip] = r.asn; });
    return m;
  }, [nonInteractive]);

  const adjacencyIndex = useMemo(
    () => buildAdjacencyIndex(ipClassifications, asnByIp),
    [ipClassifications, asnByIp]
  );

  // deviceId(lower) -> device record from the aggregate, for the panel's chips.
  const niDeviceIndex = useMemo(() => {
    const m = {};
    (nonInteractive?.devices || []).forEach((d) => { if (d?.deviceId) m[String(d.deviceId).toLowerCase()] = d; });
    return m;
  }, [nonInteractive]);


  // Resizable columns
  const COL_WIDTHS_INIT = [32, 60, 180, 200, 180, 160, 80, 55, 55, 55, 75, 55, 60, 60, 60];
  const [colWidths, setColWidths] = useState(COL_WIDTHS_INIT);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(32, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const COLUMNS = useMemo(() => [
    { id: '_expand',       label: '',              sortable: false, filterable: false, center: true  },
    { id: 'Status',        label: 'Status',        sortable: true,  filterable: true,  center: true  },
    { id: 'IPAddress',     label: 'IP Address',     sortable: true,  filterable: false, center: false },
    { id: 'Comment',       label: 'Comment',        sortable: true,  filterable: false, center: false },
    { id: 'Location',      label: 'Location',       sortable: true,  filterable: true,  center: false },
    { id: 'Provider',      label: 'ISP / Provider', sortable: true,  filterable: true,  center: false },
    { id: 'RiskLevel',     label: 'Risk Level',     sortable: true,  filterable: true,  center: false },
    { id: 'IsVPN',         label: 'VPN',            sortable: true,  filterable: true,  center: true  },
    { id: 'IsProxy',       label: 'Proxy',          sortable: true,  filterable: true,  center: true  },
    { id: 'IsTOR',         label: 'TOR',            sortable: true,  filterable: true,  center: true  },
    { id: 'IsCompromised', label: 'Compromised',    sortable: true,  filterable: true,  center: true  },
    { id: 'IsAnonymous',   label: 'Anon',           sortable: true,  filterable: true,  center: true  },
    { id: 'DRMMMatched',   label: 'DRMM',           sortable: true,  filterable: true,  center: true  },
    { id: 'IsBaseline',    label: 'Baseline',       sortable: true,  filterable: true,  center: true  },
    { id: 'EventCount',    label: 'Events',         sortable: true,  filterable: false, center: true  },
  ], []);

  const tenantSafeSet = useMemo(() => new Set(tenantSafeIPs), [tenantSafeIPs]);

  // Get effective status for an IP: explicit classification > tenant safe default > default
  const getIPStatus = useMemo(() => (ipAddress) => {
    const explicit = ipClassifications[ipAddress];
    if (explicit) return { status: explicit, source: tenantSafeSet.has(ipAddress) ? 'Tenant' : null };
    if (tenantSafeSet.has(ipAddress)) return { status: 'safe', source: 'Tenant' };
    return { status: 'default', source: null };
  }, [ipClassifications, tenantSafeSet]);

  // IPs the analyst classified malicious in this case — the set the "Add to Known
  // Malicious IPs" action promotes to the cross-tenant blocklist (deduped, table-scoped).
  const maliciousIps = useMemo(
    () => ipData.filter((ip) => getIPStatus(ip.IPAddress).status === 'malicious').map((ip) => ip.IPAddress),
    [ipData, getIPStatus]
  );
  // Malicious-classified addresses that are MICROSOFT service infrastructure (v1.189.0).
  // Promoting one puts a Microsoft address on the cross-tenant blocklist — e.g. My Signins'
  // backend, which carries hundreds of legitimate MFA registrations across tenants. The usual
  // reason it was classified malicious is to attribute a single row; "Mark as attacker
  // activity" on that row is the right tool now, so the modal warns rather than blocks.
  const microsoftMaliciousIps = useMemo(() => ipData
    .filter((ip) => getIPStatus(ip.IPAddress).status === 'malicious')
    .filter((ip) => /^MS-/.test(String(ip.AzureServiceTier || '')) || /microsoft/i.test(String(ip.Provider || '')))
    .map((ip) => ip.IPAddress), [ipData, getIPStatus]);

  // Build dropdown filter options from data
  const columnOptions = useMemo(() => {
    const opts = {};
    const statuses = new Set();
    const locations = new Set();
    const providers = new Set();
    const riskLabels = new Set();
    const boolCols = ['IsVPN', 'IsProxy', 'IsTOR', 'IsCompromised', 'IsAnonymous', 'DRMMMatched'];
    const boolSets = {};
    boolCols.forEach(c => { boolSets[c] = new Set(); });

    ipData.forEach(ip => {
      const { status } = getIPStatus(ip.IPAddress);
      statuses.add(status.charAt(0).toUpperCase() + status.slice(1));

      const loc = [ip.City, ip.State, ip.Country].filter(Boolean).join(', ');
      if (loc) locations.add(loc);

      const providerDisplay = ip.Provider || serviceIPLabelMap[ip.IPAddress] || '';
      if (providerDisplay) providers.add(providerDisplay);

      if (status !== 'default') {
        riskLabels.add(getIPStatusLabel(status));
      } else {
        riskLabels.add(getRiskLevelLabel(ip.RiskLevel));
      }

      boolCols.forEach(c => {
        boolSets[c].add(ip[c] ? 'Yes' : 'No');
      });
    });

    opts['Status'] = [...statuses].sort();
    opts['Location'] = [...locations].sort();
    opts['Provider'] = [...providers].sort();
    opts['RiskLevel'] = [...riskLabels].sort();
    boolCols.forEach(c => { opts[c] = [...boolSets[c]].sort(); });

    const baselineFilterSet = new Set();
    ipData.forEach(ip => baselineFilterSet.add(baselineIPSet.has(ip.IPAddress) ? 'Yes' : 'No'));
    opts['IsBaseline'] = [...baselineFilterSet].sort();

    return opts;
  }, [ipData, getIPStatus, baselineIPSet, serviceIPLabelMap]);

  // Pre-compute device summaries grouped by IP from sign-in events
  const deviceSummaryByIP = useMemo(() => {
    const byIP = {};
    signInEvents.forEach((evt) => {
      const ip = evt.IPAddress;
      if (!ip) return;
      if (!byIP[ip]) byIP[ip] = [];
      byIP[ip].push(evt);
    });

    const summaries = {};
    Object.entries(byIP).forEach(([ip, events]) => {
      const deviceMap = {};
      events.forEach((evt) => {
        const hostname = evt.DeviceHostname || '(none)';
        const os = evt.DeviceOS || 'Unknown';
        const browser = parseBrowserSimple(evt.UserAgent);
        const key = `${hostname}|${os}|${browser}`;

        if (!deviceMap[key]) {
          deviceMap[key] = {
            hostname,
            os,
            browser,
            apps: new Set(),
            isCompliant: null,
            firstSeen: evt.EventTimeUTC,
            lastSeen: evt.EventTimeUTC,
            successCount: 0,
            failCount: 0,
            riskMin: null,
            riskMax: null,
            drmmMatch: null,
          };
        }
        const d = deviceMap[key];

        const appName = evt.ApplicationName || evt.AppDisplayName || 'Unknown';
        d.apps.add(appName);

        if (evt.IsDeviceCompliant === true) d.isCompliant = true;
        else if (d.isCompliant === null && evt.IsDeviceCompliant === false) d.isCompliant = false;

        if (evt.DRMM_HostnameMatched === true) d.drmmMatch = true;
        else if (d.drmmMatch === null && evt.DRMM_HostnameMatched === false) d.drmmMatch = false;

        if (evt.EventTimeUTC && evt.EventTimeUTC < d.firstSeen) d.firstSeen = evt.EventTimeUTC;
        if (evt.EventTimeUTC && evt.EventTimeUTC > d.lastSeen) d.lastSeen = evt.EventTimeUTC;

        const status = (evt.ResultStatus || '').toLowerCase();
        if (status === 'success' || status === 'succeeded') d.successCount++;
        else if (status === 'failed' || status === 'failure') d.failCount++;

        const risk = evt.RiskScore ?? evt.Risk_Total;
        if (risk != null) {
          if (d.riskMin === null || risk < d.riskMin) d.riskMin = risk;
          if (d.riskMax === null || risk > d.riskMax) d.riskMax = risk;
        }
      });

      summaries[ip] = Object.values(deviceMap)
        .map((d) => ({ ...d, apps: [...d.apps] }))
        .sort((a, b) => (b.lastSeen || '').localeCompare(a.lastSeen || ''));
    });

    return summaries;
  }, [signInEvents]);

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) { next[columnId] = value; } else { delete next[columnId]; }
      return next;
    });
  }, []);

  const activeFilterCount = Object.keys(columnFilters).length;

  const filteredData = useMemo(() => {
    let result = ipData.map(ip => ({
      ...ip,
      IsBaseline: baselineIPSet.has(ip.IPAddress),
      _comment: ipComments[ip.IPAddress] || '',
    }));

    // Text search filter
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(
        (ip) =>
          (ip.IPAddress && ip.IPAddress.toLowerCase().includes(q)) ||
          (ip.City && ip.City.toLowerCase().includes(q)) ||
          (ip.State && ip.State.toLowerCase().includes(q)) ||
          (ip.Country && ip.Country.toLowerCase().includes(q)) ||
          ((ip.Provider || serviceIPLabelMap[ip.IPAddress] || '').toLowerCase().includes(q)) ||
          (ip._comment && ip._comment.toLowerCase().includes(q))
      );
    }

    // Column filters
    if (columnFilters['Status']) {
      const v = columnFilters['Status'].toLowerCase();
      result = result.filter(ip => getIPStatus(ip.IPAddress).status === v);
    }
    if (columnFilters['Location']) {
      const v = columnFilters['Location'];
      result = result.filter(ip => {
        const loc = [ip.City, ip.State, ip.Country].filter(Boolean).join(', ');
        return loc === v;
      });
    }
    if (columnFilters['Provider']) {
      result = result.filter(ip => (ip.Provider || serviceIPLabelMap[ip.IPAddress] || '') === columnFilters['Provider']);
    }
    if (columnFilters['RiskLevel']) {
      const v = columnFilters['RiskLevel'];
      result = result.filter(ip => {
        const { status } = getIPStatus(ip.IPAddress);
        const label = status !== 'default' ? getIPStatusLabel(status) : getRiskLevelLabel(ip.RiskLevel);
        return label === v;
      });
    }
    const boolCols = ['IsVPN', 'IsProxy', 'IsTOR', 'IsCompromised', 'IsAnonymous', 'DRMMMatched', 'IsBaseline'];
    boolCols.forEach(c => {
      if (columnFilters[c]) {
        const wantTrue = columnFilters[c] === 'Yes';
        result = result.filter(ip => !!ip[c] === wantTrue);
      }
    });

    // Non-interactive view filter. `new` = rows that exist ONLY because the aggregate
    // put them there (no events in this case at all) — the set the acceptance test cares
    // about; `seen` = every IP with non-interactive activity, including ones the case
    // already had.
    if (niFilter) {
      result = result.filter(ip => {
        if (niFilter === 'new')  return !!ip.NonInteractiveOnly;
        if (niFilter === 'seen') return !!ip.NonInteractive;
        return true;
      });
    }

    // Real-user evidence tier filter
    if (evidenceFilter) {
      result = result.filter(ip => {
        const e = evidenceByIp[String(ip.IPAddress || '').toLowerCase()];
        const tier = e?.tier ?? null;
        if (evidenceFilter === 'managed')    return tier === 1;
        if (evidenceFilter === 'registered') return tier === 2;
        if (evidenceFilter === 'any')        return tier === 1 || tier === 2;
        if (evidenceFilter === 'caution')    return !!e?.caution;
        return true;
      });
    }

    // Sort
    const STATUS_PRIORITY = { malicious: 3, suspect: 2, safe: 1, default: 0 };
    result.sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'Status') {
        aVal = STATUS_PRIORITY[getIPStatus(a.IPAddress).status] ?? 0;
        bVal = STATUS_PRIORITY[getIPStatus(b.IPAddress).status] ?? 0;
      } else if (sortColumn === 'Location') {
        aVal = [a.City, a.State, a.Country].filter(Boolean).join(', ');
        bVal = [b.City, b.State, b.Country].filter(Boolean).join(', ');
      } else if (sortColumn === 'Provider') {
        aVal = a.Provider || '';
        bVal = b.Provider || '';
      } else if (sortColumn === 'Comment') {
        aVal = a._comment || '';
        bVal = b._comment || '';
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        comparison = (aVal ? 1 : 0) - (bVal ? 1 : 0);
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [ipData, filterQuery, sortColumn, sortDirection, columnFilters, getIPStatus, baselineIPSet, serviceIPLabelMap, evidenceFilter, evidenceByIp, ipComments, niFilter]);

  // IPs eligible for the conservative bulk "Mark Safe": currently-filtered, graded
  // tier 1/2, NOT flagged caution (network-novel / recently-registered), not already Safe.
  const bulkSafeIps = useMemo(() => filteredData
    .filter(ip => {
      const e = evidenceByIp[String(ip.IPAddress || '').toLowerCase()];
      if (!e || e.tier === 0 || e.caution) return false;
      return getIPStatus(ip.IPAddress).status !== 'safe';
    })
    .map(ip => ip.IPAddress),
    [filteredData, evidenceByIp, getIPStatus]);

  useEffect(() => { setCurrentPage(1); setExpandedRow(null); }, [filteredData]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  function getLocation(ip) {
    const parts = [ip.City, ip.State, ip.Country].filter(Boolean);
    return parts.join(', ') || '\u2014';
  }

  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const pagedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (ipData.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">No IP address data found.</p>
      </div>
    );
  }

  const SortIndicator = ({ column }) => {
    if (sortColumn !== column) return null;
    return (
      <span className="ml-1 text-blue-400">
        {sortDirection === 'asc' ? '\u25B2' : '\u25BC'}
      </span>
    );
  };

  return (
    <div>
      {/* Filter and Summary */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2 flex-1">
          {onFetchNonInteractive && (
            <button
              type="button"
              onClick={doFetchNonInteractive}
              disabled={DEMO || niRunning}
              title={DEMO
                ? DEMO_UNAVAILABLE_TITLE
                : 'Query the non-interactive Entra sign-in feed (token refresh / SSO reuse / PRT replay) for this case. That feed is a separate Delta table the investigation does not read, so IPs and devices used only for token replay are missing from this table until you run this.'}
              className="flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg border transition-colors whitespace-nowrap shrink-0 bg-sky-950/30 border-sky-700/50 text-sky-300 hover:bg-sky-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BoltIcon className={`h-3.5 w-3.5 ${niRunning ? 'animate-pulse' : ''}`} />
              {niRunning ? 'Querying…' : 'Non-interactive sign-ins'}
            </button>
          )}
          {nonInteractive?.ips?.length > 0 && (
            <select
              value={niFilter}
              onChange={(e) => { setNiFilter(e.target.value); setCurrentPage(1); }}
              title="Filter by non-interactive sign-in activity"
              className={`text-xs px-2 py-2 rounded-lg border bg-gray-800 cursor-pointer focus:outline-none shrink-0 ${niFilter ? 'border-sky-600 text-sky-300' : 'border-gray-700 text-gray-400'}`}
            >
              <option value="">Non-interactive: all IPs</option>
              <option value="new">New to this case</option>
              <option value="seen">Any non-interactive activity</option>
            </select>
          )}
          {onRefreshIpEnrichmentBatch && (
            <button
              type="button"
              onClick={doBatchRefresh}
              disabled={DEMO || batchRunning || unenrichedIps.length === 0}
              title={DEMO
                ? DEMO_UNAVAILABLE_TITLE
                : 'Fetch IP enrichment from ProxyCheck.io for every IP in this table that has no location/ISP data yet'}
              className="flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg border transition-colors whitespace-nowrap shrink-0 bg-gray-800 border-gray-700 text-gray-300 hover:text-sky-300 hover:border-sky-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`h-3.5 w-3.5 ${batchRunning ? 'animate-spin' : ''}`} />
              {batchRunning ? 'Enriching…' : `Enrich unenriched (${unenrichedIps.length})`}
            </button>
          )}
          {isAdmin && onFetchRealUserEvidence && (
            <button
              type="button"
              onClick={doFetchEvidence}
              disabled={evidenceRunning || evidenceCandidateIps.length === 0}
              title="Grade every IP on whether the user's own device/token history proves it's the real user (vs. a potential attacker)"
              className="flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg border transition-colors whitespace-nowrap shrink-0 bg-gray-800 border-gray-700 text-gray-300 hover:text-emerald-300 hover:border-emerald-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldCheckIcon className={`h-3.5 w-3.5 ${evidenceRunning ? 'animate-pulse' : ''}`} />
              {evidenceRunning ? 'Checking…' : `Check real-user evidence (${evidenceCandidateIps.length})`}
            </button>
          )}
          {hasEvidence && (
            <select
              value={evidenceFilter}
              onChange={(e) => setEvidenceFilter(e.target.value)}
              title="Filter by real-user evidence tier"
              className={`text-xs px-2 py-2 rounded-lg border bg-gray-800 cursor-pointer focus:outline-none shrink-0 ${evidenceFilter ? 'border-emerald-600 text-emerald-300' : 'border-gray-700 text-gray-400'}`}
            >
              <option value="">Real-user: all</option>
              <option value="any">Real user (managed + registered)</option>
              <option value="managed">Real user · managed</option>
              <option value="registered">Real user · registered</option>
              <option value="caution">⚠ Caution (verify)</option>
            </select>
          )}
          {hasEvidence && onSetIPsSafe && (
            <button
              type="button"
              onClick={() => { if (bulkSafeIps.length > 0) onSetIPsSafe(bulkSafeIps); }}
              disabled={bulkSafeIps.length === 0}
              title="Mark the shown real-user IPs (tier 1/2, no caution, not already Safe) as Safe"
              className="flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg border transition-colors whitespace-nowrap shrink-0 bg-green-950/30 border-green-700/50 text-green-300 hover:bg-green-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <CheckCircleIcon className="h-3.5 w-3.5" />
              {`Mark Safe (${bulkSafeIps.length})`}
            </button>
          )}
          {isAdmin && onAddMaliciousToKnownList && maliciousIps.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAddMalicious(true)}
              title="Add every IP marked malicious in this case to the cross-tenant Known Malicious IPs blocklist"
              className="flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg border transition-colors whitespace-nowrap shrink-0 bg-red-950/30 border-red-700/50 text-red-300 hover:bg-red-900/40"
            >
              <NoSymbolIcon className="h-3.5 w-3.5" />
              {`Add ${maliciousIps.length} malicious to blocklist`}
            </button>
          )}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter IPs, location, ISP..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          {batchResult && (
            <span
              title={batchResult.error || ''}
              className={`text-xs ${batchResult.error ? 'text-red-400 max-w-md break-words' : 'text-gray-400 whitespace-nowrap'}`}
            >
              {batchResult.error
                ? batchResult.error
                : `${batchResult.fetched} fetched, ${batchResult.surfaced} cached${batchResult.failed ? `, ${batchResult.failed} failed` : ''}`}
            </span>
          )}
          {evidenceResult && (
            <span
              title={evidenceResult.error || ''}
              className={`text-xs ${evidenceResult.error ? 'text-red-400 max-w-md break-words' : 'text-gray-400 whitespace-nowrap'}`}
            >
              {evidenceResult.error
                ? evidenceResult.error
                : `real-user evidence: ${evidenceResult.tier1} managed · ${evidenceResult.tier2} registered · ${evidenceResult.tier0} none`}
            </span>
          )}
          {niResult && (
            <span className={`text-xs whitespace-nowrap ${niResult.error ? 'text-red-400' : 'text-gray-400'}`}>
              {niResult.error
                ? niResult.error
                : `non-interactive: ${niResult.found} IP${niResult.found === 1 ? '' : 's'} · ${niResult.added} new to this case · ${niResult.devices} device${niResult.devices === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {activeFilterCount > 0 && (
            <button onClick={() => setColumnFilters({})} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
              Clear column filters
            </button>
          )}
          <span>
            {filteredData.length} IP{filteredData.length !== 1 ? 's' : ''}
          </span>
          {isSaving && <span className="text-blue-400">Saving...</span>}
          {!isSaving && (() => {
            const counts = { safe: 0, suspect: 0, malicious: 0 };
            ipData.forEach(ip => {
              const s = getIPStatus(ip.IPAddress).status;
              if (counts[s] !== undefined) counts[s]++;
            });
            return (
              <>
                {counts.safe > 0 && <span className="text-green-400">{counts.safe} safe</span>}
                {counts.suspect > 0 && <span className="text-amber-400">{counts.suspect} suspect</span>}
                {counts.malicious > 0 && <span className="text-red-400">{counts.malicious} malicious</span>}
              </>
            );
          })()}
        </div>
      </div>

      {/* Collection-state disclosures for the non-interactive feed (v1.147.0).
          Both are artefacts of what was COLLECTED, not of what happened, and both would
          otherwise be read as findings — a later first-seen as "the attack started then",
          a zero failure count as "the attacker never failed". Stated once, above the rows,
          because they qualify every row equally. */}
      {nonInteractive?.ips?.length > 0 && <NonInteractiveGuidance />}

      {/* A reduced or failed save must never be silent: the analyst is looking at data that
          will not survive their next reload, and only they can decide whether that matters.
          The Table Storage property cap is ~32,768 chars and exceeding it 400s the whole
          entity write, so shedding detail is the correct behaviour - hiding it is not. */}
      {nonInteractive?.persist?.reduced && (
        <div className="mb-3 px-3 py-2 rounded-lg border border-amber-900/40 bg-amber-950/10">
          <p className="text-xs text-amber-300">
            <span className="font-semibold">
              {nonInteractive.persist.failed ? 'Not saved to this case:' : 'Saved in reduced form:'}
            </span>{' '}
            {nonInteractive.persist.failed ? (
              <>
                this result is too large to store on the case, so it will disappear when you reload.
                {' '}{nonInteractive.persist.note}
              </>
            ) : (
              <>
                every IP and device was kept, but {nonInteractive.persist.dropped} was dropped to fit the
                per-case storage limit. {nonInteractive.persist.note}
              </>
            )}
          </p>
        </div>
      )}

      {nonInteractive && (nonInteractive.coverage?.startsAfterWindow || nonInteractive.failureCoverage?.failuresComparable === false) && (
        <div className="mb-3 px-3 py-2 rounded-lg border border-amber-900/40 bg-amber-950/10 space-y-1">
          {nonInteractive.coverage?.startsAfterWindow && (
            <p className="text-xs text-amber-300">
              <span className="font-semibold">Partial coverage:</span> the non-interactive feed holds
              no data for this tenant before {nonInteractive.coverage.feedFirstDate} — the first{' '}
              {nonInteractive.coverage.uncoveredDays} day
              {nonInteractive.coverage.uncoveredDays === 1 ? '' : 's'} of this case&apos;s window are not
              covered. Treat every first-seen below as <span className="italic">earliest observed</span>,
              not as the start of activity.
            </p>
          )}
          {nonInteractive.failureCoverage?.failuresComparable === false && (
            <p className="text-xs text-amber-300">
              <span className="font-semibold">Failure rows are uneven across this window:</span>{' '}
              {!nonInteractive.failureCoverage.measured ? (
                <>
                  coverage could not be measured for this window, so a zero failure count carries no
                  meaning either way.
                </>
              ) : nonInteractive.failureCoverage.daysWithFailures === 0 ? (
                /* ZERO is its own case. The partial-coverage wording ("outside that range")
                   is meaningless when there is no range, and worse, it understates: this is
                   not an uneven window, it is a window with NO failure data at all. Every
                   outcome-based read - containment, the real-user-re-authenticates test - is
                   unavailable here, and saying so is the whole point. */
                <>
                  <span className="text-gray-300">no</span> failed non-interactive sign-ins were
                  collected for this tenant on any of the {niFailDays} day
                  {niFailDays === 1 ? '' : 's'}{' '}
                  {niFailSampled
                    ? `sampled across this window's ${nonInteractive.failureCoverage.daysInWindow} days`
                    : 'in this window'}
                  . That is a
                  collection gap, <span className="italic">not</span> evidence that nothing was attempted
                  and rejected — this feed collected successes only for much of its life. Treat every
                  success count below as complete and every failure count as unknown, and do not read
                  &ldquo;still succeeding&rdquo; or &ldquo;stopped succeeding&rdquo; as a containment
                  result for this window.
                  {niFailuresRecoverable && (
                    <>
                      {' '}
                      <span className="text-gray-300">
                        This window is still inside Microsoft&apos;s ~30-day sign-in retention, so the
                        failure rows can still be recovered by a backfill
                      </span>{' '}
                      — after which the outcome checks would work here. That option expires as the window
                      ages out.
                    </>
                  )}
                </>
              ) : (
                <>
                  failed non-interactive sign-ins appear on only{' '}
                  {nonInteractive.failureCoverage.daysWithFailures} of {niFailDays}{' '}
                  {niFailSampled
                    ? `days sampled across this window's ${nonInteractive.failureCoverage.daysInWindow}`
                    : 'days held for this tenant'}
                  {nonInteractive.failureCoverage.failureFirstDate && (
                    <>
                      , the earliest {niFailSampled ? 'sampled day with failures being' : 'on'}{' '}
                      {nonInteractive.failureCoverage.failureFirstDate}
                    </>
                  )}
                  . On the days outside that range a low or zero failure count may be a collection
                  artefact rather than evidence that nothing was attempted and rejected.
                </>
              )}
            </p>
          )}
          {nonInteractive.failureCoverage?.measured
            && nonInteractive.failureCoverage.failuresComparable
            && nonInteractive.failureCoverage.failuresObserved && (
            <p className="text-xs text-gray-400">
              <span className="font-semibold text-gray-300">Failures are collected for this window</span>{' '}
              ({nonInteractive.failureCoverage.daysWithFailures} of {niFailDays}{' '}
              {niFailSampled ? 'days sampled' : 'days'}), so an outcome shown below is a real
              outcome. A <span className="font-mono">50173</span> after a credential reset means the
              token grant was revoked — the real user re-authenticates and resumes succeeding, a stolen
              token cannot.
            </p>
          )}
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', minWidth: colWidths.reduce((a, b) => a + b, 0) + 'px' }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {COLUMNS.map((col, i) => (
                  <th
                    key={col.id}
                    ref={el => { thRefs.current[i] = el; }}
                    style={{ width: colWidths[i] + 'px' }}
                    onClick={col.sortable ? () => handleSort(col.id) : undefined}
                    className={`relative text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 select-none whitespace-nowrap overflow-hidden ${
                      col.center ? 'text-center' : 'text-left'
                    } ${col.sortable ? 'cursor-pointer hover:text-gray-300' : ''}`}
                  >
                    <span className={`inline-flex items-center ${col.center ? 'justify-center' : ''}`}>
                      {col.label}
                      <SortIndicator column={col.id} />
                    </span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, i)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row with <select> dropdowns */}
              <tr className="border-b border-gray-700 bg-gray-900/50">
                {COLUMNS.map((col, i) => (
                  <th key={`filter-${col.id}`} className="px-3 py-1.5" style={{ width: colWidths[i] + 'px' }}>
                    {col.filterable ? (
                      <select
                        value={columnFilters[col.id] || ''}
                        onChange={(e) => handleColumnFilterChange(col.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full bg-gray-800 border rounded px-1 py-1 text-xs focus:outline-none focus:border-blue-600 cursor-pointer ${
                          columnFilters[col.id] ? 'border-blue-600 text-blue-300' : 'border-gray-700 text-gray-400'
                        }`}
                      >
                        <option value="">All</option>
                        {(columnOptions[col.id] || []).map(opt => (
                          <option key={opt} value={opt}>{opt || '(empty)'}</option>
                        ))}
                      </select>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedData.map((ip, index) => {
                const { status, source } = getIPStatus(ip.IPAddress);
                const statusBorder = getIPStatusBorderClass(status);
                const rowBorder = statusBorder || getRiskBorderClass(ip.RiskLevel);
                const isExpanded = expandedRow === index;
                const devices = deviceSummaryByIP[ip.IPAddress] || [];
                const ev = evidenceByIp[String(ip.IPAddress || '').toLowerCase()];
                const ni = ip.NonInteractive || null;
                const adj = adjacencyFor(ip.IPAddress, adjacencyIndex);
                const adjText = adjacencyBadgeText(adj);

                return (
                  <React.Fragment key={ip.IPAddress}>
                    <tr
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                      className={`hover:bg-gray-800/50 cursor-pointer transition-colors ${rowBorder}`}
                    >
                      {/* Expand Chevron */}
                      <td className="pl-3 pr-1 py-3 text-center" style={{ width: 32 }}>
                        <ChevronRightIcon
                          className={`h-3.5 w-3.5 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </td>

                      {/* Status Toggle */}
                      <td className="px-3 py-3 text-center">
                        <StatusCircle
                          tone={ipStatusTone(status)}
                          onClick={() => onCycleIPStatus(ip.IPAddress)}
                          title={`${getIPStatusLabel(status) || 'Default'} — click to cycle`}
                        />
                        {source === 'Tenant' && (
                          <span className="block text-[9px] text-gray-600 leading-none mt-0.5">T</span>
                        )}
                      </td>

                      {/* IP Address */}
                      <td className={`px-4 py-3 font-mono text-xs whitespace-nowrap ${getIPStatusTextClass(status)} group`}>
                        {ip.IPAddress}
                        <CopyButton text={ip.IPAddress} />
                        {onRefreshIpEnrichment && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); doRefreshIp(ip.IPAddress); }}
                            disabled={DEMO || refreshingIp === ip.IPAddress}
                            title={DEMO
                              ? DEMO_UNAVAILABLE_TITLE
                              : `Re-fetch IP enrichment from ProxyCheck.io for ${ip.IPAddress}`}
                            className="ml-1 inline-flex items-center text-gray-600 hover:text-sky-300 disabled:opacity-50 align-middle"
                          >
                            <ArrowPathIcon className={`h-3.5 w-3.5 ${refreshingIp === ip.IPAddress ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        {refreshError?.ip === ip.IPAddress && (
                          <div className="text-[10px] text-red-400 font-sans mt-0.5 whitespace-normal">
                            {refreshError.msg}
                          </div>
                        )}
                        {ev && ev.tier > 0 && (
                          <div className="mt-0.5">
                            <TierPill ev={ev} />
                          </div>
                        )}
                        {/* Provenance. Without this an analyst classifies an IP, goes
                            looking for its activity in the timeline, finds nothing, and
                            reasonably concludes the portal is broken. */}
                        {markEmailOnlyIPs && ip.IsEmailOnlyIP && (
                          <div className="mt-0.5">
                            <span
                              title="This IP appears only in message-trace (email) events on this case — never in a sign-in, Exchange or SharePoint event. It is shown here so it can still be classified."
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-700 bg-gray-800 text-gray-400 text-[10px] font-sans whitespace-nowrap"
                            >
                              email-only
                            </span>
                          </div>
                        )}
                        {ip.NonInteractiveOnly && (
                          <div className="mt-0.5">
                            <span
                              title="This IP reached the case only from the non-interactive sign-in feed (token refresh / SSO reuse / PRT replay). It has no events in the case timeline — expand the row for its evidence."
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sky-800/60 bg-sky-950/40 text-sky-300 text-[10px] font-sans whitespace-nowrap"
                            >
                              non-interactive only
                            </span>
                          </div>
                        )}
                        {adjText && (
                          <div className="mt-0.5">
                            <span
                              title={`Attacker infrastructure clusters. ${adj.prefixPeers.concat(adj.asnPeers).join(', ')} ${adj.prefixPeers.concat(adj.asnPeers).length === 1 ? 'is' : 'are'} already classified on this case.`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-red-800/60 bg-red-950/40 text-red-300 text-[10px] font-sans whitespace-nowrap"
                            >
                              <ExclamationTriangleIcon className="h-3 w-3" />
                              {adjText}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Comment (editable — IP Addresses tab is the only edit surface) */}
                      <td className="px-3 py-3 align-middle">
                        <CommentCell
                          comment={ip._comment}
                          editable={!!onUpdateIPComment}
                          onSave={(text) => onUpdateIPComment && onUpdateIPComment(ip.IPAddress, text)}
                        />
                      </td>

                      {/* Location */}
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                        {getLocation(ip)}
                      </td>

                      {/* ISP */}
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap max-w-[200px] truncate">
                        {ip.Provider || serviceIPLabelMap[ip.IPAddress] || '\u2014'}
                      </td>

                      {/* Risk Level */}
                      <td className="px-4 py-3">
                        {status !== 'default' ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getIPStatusBgClass(status)}`}>
                            {getIPStatusLabel(status)}
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${getRiskLevelBadge(ip.RiskLevel)}`}>
                            {getRiskLevelLabel(ip.RiskLevel)}
                          </span>
                        )}
                      </td>

                      {/* VPN Flag */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={ip.IsVPN}
                          label="VPN"
                          colorClass="bg-amber-900/60 text-amber-300"
                        />
                      </td>

                      {/* Proxy Flag */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={ip.IsProxy}
                          label="Proxy"
                          colorClass="bg-amber-900/60 text-amber-300"
                        />
                      </td>

                      {/* TOR Flag */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={ip.IsTOR}
                          label="TOR"
                          colorClass="bg-red-900/60 text-red-300"
                        />
                      </td>

                      {/* Compromised Flag */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={ip.IsCompromised}
                          label="Botnet"
                          colorClass="bg-red-900/60 text-red-300"
                        />
                      </td>

                      {/* Anonymous Flag */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={ip.IsAnonymous}
                          label="Anon"
                          colorClass="bg-amber-900/60 text-amber-300"
                        />
                      </td>

                      {/* DRMM Match */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={ip.DRMMMatched}
                          label="Match"
                          colorClass="bg-green-900/60 text-green-300"
                        />
                      </td>

                      {/* Baseline Known */}
                      <td className="px-3 py-3 text-center">
                        <FlagBadge
                          active={baselineIPSet.has(ip.IPAddress)}
                          label="Known"
                          colorClass="bg-blue-900/60 text-blue-300"
                        />
                      </td>

                      {/* Event Count */}
                      <td className="px-4 py-3 text-gray-300 text-center font-mono">
                        {ip.EventCount ?? '\u2014'}
                      </td>
                    </tr>

                    {/* Expanded device detail row */}
                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={COLUMNS.length} className="px-6 py-4">
                          {ev && (
                            <RealUserEvidencePanel
                              ev={ev}
                              status={status}
                              onMarkSafe={onSetIPSafe ? () => onSetIPSafe(ip.IPAddress) : null}
                            />
                          )}
                          {ni && (
                            <NonInteractiveEvidencePanel
                              row={ni}
                              adjacency={adj}
                              deviceIndex={niDeviceIndex}
                              caseDeviceNames={caseDeviceNames}
                              caseStartDate={caseStartDate}
                              iocDate={iocDate}
                              firstAttackerActivity={firstAttackerActivity}
                              hasMaliciousClassification={hasMaliciousClassification}
                              sessionIndex={sessionIndex}
                              activityInWindow={activityInWindow}
                              coverage={nonInteractive?.coverage || null}
                              failureCoverage={nonInteractive?.failureCoverage || null}
                            />
                          )}
                          <IPDeviceSummary devices={devices} ipAddress={ip.IPAddress} />
                          {/* "Who else used this IP?" — ON DEMAND. Open to every portal
                              user: the server scopes cross-tenant identities to the
                              caller's readable set and returns counts only for the rest,
                              so this is never a visibly-live control that 403s
                              (memory feedback_ui_open_api_gated_silent_button). */}
                          {tenantId && (() => {
                            const key = String(ip.IPAddress || '').toLowerCase();
                            const users = ipUsersByIp[key];
                            const err = ipUsersErrByIp[key];
                            const loading = ipUsersLoadingIp === ip.IPAddress;
                            return (
                              <div className="mt-3 pt-3 border-t border-gray-700/50 text-xs">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-gray-400 font-medium">Other users of this address</span>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); loadIpUsers(ip.IPAddress); }}
                                    disabled={loading}
                                    title={`Which other users signed in from ${ip.IPAddress} in the last 30 days — this tenant, plus other tenants you can access`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-blue-500/40 bg-blue-950/30 text-blue-300 hover:bg-blue-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <UsersIcon className="h-3 w-3" />
                                    {loading ? 'Loading…' : (users ? 'Refresh' : 'Check other users')}
                                  </button>
                                </div>
                                {err && <div className="mt-1 text-red-400">User lookup failed: {err}</div>}
                                {users && (
                                  <IpPrevalenceUsers
                                    data={users}
                                    ip={ip.IPAddress}
                                    className="mt-1.5"
                                  />
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredData.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedRow(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedRow(null); }}
        />
      </div>

      {showAddMalicious && (
        <AddMaliciousToBlocklistModal
          ips={maliciousIps}
          microsoftIps={microsoftMaliciousIps}
          caseId={caseId}
          onConfirm={onAddMaliciousToKnownList}
          onClose={() => setShowAddMalicious(false)}
        />
      )}
    </div>
  );
}

/**
 * Confirmation modal for promoting the case's malicious-classified IPs to the
 * cross-tenant Known Malicious IPs blocklist. Lists every IP to add, previews the
 * type/notes that will be applied, warns on large batches, and reports the result.
 */
function AddMaliciousToBlocklistModal({ ips = [], microsoftIps = [], caseId, onConfirm, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const highCount = ips.length > 10;
  const notesPreview = `per case ${caseId}`;

  const handleConfirm = async () => {
    if (!onConfirm || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await onConfirm(ips);
      setResult({
        added:   resp?.added ?? 0,
        updated: resp?.updated ?? 0,
        failed:  resp?.failed ?? 0,
      });
    } catch (e) {
      setError(e?.message || 'Failed to add IPs to the blocklist.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-2.5">
            <NoSymbolIcon className="h-5 w-5 text-red-400 shrink-0" />
            <h3 className="text-sm font-semibold text-gray-100">Add malicious IPs to Known Malicious IPs</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-300 shrink-0">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          {result ? (
            <div className="text-sm text-gray-300">
              <div className="flex items-center gap-2 text-green-300 mb-2">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="font-medium">Done.</span>
              </div>
              <p className="text-gray-400">
                {result.added} added, {result.updated} updated
                {result.failed > 0 ? <span className="text-red-400">, {result.failed} failed</span> : null}.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-400 mb-3">
                These {ips.length} IP{ips.length !== 1 ? 's' : ''} marked malicious in this case will be
                added to the cross-tenant blocklist as{' '}
                <span className="text-gray-200 font-medium">type “attacker”</span> with notes{' '}
                <span className="text-gray-200 font-mono text-xs">“{notesPreview}”</span>. Existing entries
                are overwritten and their added date refreshed.
              </p>

              {microsoftIps.length > 0 && (
                <div className="mb-3 p-2.5 bg-red-900/30 border border-red-700/50 rounded-lg text-red-200 text-xs flex items-start gap-2" data-testid="blocklist-microsoft-warning">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    {microsoftIps.length === 1 ? 'This address belongs' : 'These addresses belong'} to Microsoft service
                    infrastructure: <span className="font-mono">{microsoftIps.join(', ')}</span>. The blocklist is shared
                    across every tenant, and Microsoft addresses also carry other users&rsquo; legitimate activity. If the
                    address was classified Malicious only to attribute one event, use &ldquo;Mark as attacker activity&rdquo;
                    on that event in the timeline instead.
                  </span>
                </div>
              )}

              {highCount && (
                <div className="mb-3 p-2.5 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-xs flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  <span>Warning: High number of IP addresses, are you sure?</span>
                </div>
              )}

              <div className="border border-gray-800 rounded-lg divide-y divide-gray-800/70 max-h-56 overflow-y-auto">
                {ips.map((ip) => (
                  <div key={ip} className="px-3 py-1.5 font-mono text-xs text-red-300">{ip}</div>
                ))}
              </div>

              {error && (
                <div className="mt-3 p-2.5 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-xs flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {result ? (
            <button type="button" onClick={onClose} className="btn-secondary py-1.5 text-sm">Close</button>
          ) : (
            <>
              <button type="button" onClick={onClose} disabled={submitting} className="btn-secondary py-1.5 text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting || ips.length === 0}
                className="btn-danger flex items-center gap-2 py-1.5 text-sm"
              >
                {submitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <NoSymbolIcon className="h-4 w-4" />}
                {submitting ? 'Adding…' : `Add ${ips.length} to blocklist`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small tier badge shown next to an IP once real-user evidence is fetched. */
function TierPill({ ev }) {
  if (!ev || ev.tier === 0) return null;
  const caution = !!ev.caution;
  const cls = caution
    ? 'bg-amber-900/60 text-amber-300 border border-amber-700/50'
    : ev.tier === 1
      ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/50'
      : 'bg-sky-900/60 text-sky-300 border border-sky-700/50';
  const label = caution
    ? `⚠ Verify · ${ev.tier === 1 ? 'managed' : 'registered'}`
    : (ev.tierLabel || (ev.tier === 1 ? 'Real user · managed' : 'Real user · registered'));
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-sans font-medium ${cls}`}
      title={(ev.reasons || []).join(' · ')}
    >
      <ShieldCheckIcon className="h-3 w-3" />
      {label}
    </span>
  );
}

/** Expanded-row panel: real-user evidence facts + a manual "Mark Safe" confirm. */
function RealUserEvidencePanel({ ev, status, onMarkSafe }) {
  if (!ev) return null;
  const tier = ev.tier ?? 0;
  const headerCls = ev.caution ? 'text-amber-300' : tier === 1 ? 'text-emerald-300' : tier === 2 ? 'text-sky-300' : 'text-gray-400';
  const Fact = ({ label, value }) => (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-gray-600">{label}</span>
      <span className="text-gray-300">{value === '' || value == null ? '—' : String(value)}</span>
    </div>
  );
  return (
    <div className="mb-3 rounded-lg border border-gray-700 bg-gray-900/50 p-3">
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className={`text-xs font-semibold uppercase tracking-wide ${headerCls}`}>
          Real-user evidence — {ev.caution ? '⚠ verify' : (ev.tierLabel || (tier === 0 ? 'No device evidence' : ''))}
        </p>
        {tier > 0 && onMarkSafe && status !== 'safe' && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMarkSafe(); }}
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border whitespace-nowrap ${ev.caution
              ? 'border-amber-600/50 bg-amber-950/30 text-amber-300 hover:bg-amber-900/40'
              : 'border-green-600/50 bg-green-950/30 text-green-300 hover:bg-green-900/40'}`}
          >
            <CheckCircleIcon className="h-3.5 w-3.5" /> {ev.caution ? 'Mark Safe anyway' : 'Mark Safe'}
          </button>
        )}
        {status === 'safe' && <span className="text-[11px] text-green-400 whitespace-nowrap">Classified Safe</span>}
      </div>
      {tier > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs">
          <Fact label="Device" value={ev.deviceName} />
          <Fact label="Trust type" value={ev.trustType} />
          <Fact label="Compliant" value={ev.isCompliant ? 'Yes' : 'No'} />
          <Fact label="Entra-registered" value={ev.registered ? 'Yes' : 'No'} />
          <Fact label="ASN" value={ev.asn} />
          <Fact label="PRT uses" value={ev.prtCount} />
          <Fact label="Distinct apps" value={ev.appCount} />
          <Fact label="Days seen" value={ev.days} />
          <Fact label="Interactive MFA" value={ev.interactiveSuccess} />
        </div>
      )}
      {ev.reasons && ev.reasons.length > 0 && (
        <ul className="mt-2 text-[11px] text-gray-400 list-disc list-inside space-y-0.5">
          {ev.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      <p className="mt-2 text-[10px] text-gray-600 leading-relaxed">
        From the user's own non-interactive + interactive sign-in device binding (last 30d), corroborated by the Entra device registry.
        Proxy-style AiTM won't appear here, and history is shallow until the feed accumulates — confirm before classifying Safe (high-confidence evidence, not proof).
      </p>
    </div>
  );
}

/** Compact device summary sub-table for expanded IP rows */
function IPDeviceSummary({ devices, ipAddress }) {
  if (!devices || devices.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No sign-in events found within selected date range for {ipAddress}
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs font-medium text-gray-500 mb-2">
        Devices seen from this IP ({devices.length} unique combination{devices.length !== 1 ? 's' : ''})
      </p>
      <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-700 text-gray-500">
              <th className="px-3 py-2 text-left font-medium">Device</th>
              <th className="px-3 py-2 text-left font-medium">OS</th>
              <th className="px-3 py-2 text-left font-medium">Browser / Client</th>
              <th className="px-3 py-2 text-left font-medium">Application(s)</th>
              <th className="px-3 py-2 text-center font-medium">Intune Device Compliant</th>
              <th className="px-3 py-2 text-center font-medium">DRMM Name Match</th>
              <th className="px-3 py-2 text-left font-medium">First Seen</th>
              <th className="px-3 py-2 text-left font-medium">Last Seen</th>
              <th className="px-3 py-2 text-center font-medium">Success</th>
              <th className="px-3 py-2 text-center font-medium">Failed</th>
              <th className="px-3 py-2 text-center font-medium">Custom Risk Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/50">
            {devices.map((d, i) => (
              <tr key={i} className="hover:bg-gray-800/30">
                <td className="px-3 py-2 text-gray-300 font-mono whitespace-nowrap">
                  {d.hostname === '(none)' ? (
                    <span className="text-gray-600 italic">none</span>
                  ) : d.hostname}
                </td>
                <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{d.os}</td>
                <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{d.browser}</td>
                <td className="px-3 py-2 text-gray-400 max-w-[200px]">
                  <span className="line-clamp-2" title={d.apps.join(', ')}>
                    {d.apps.join(', ')}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  {d.isCompliant === true ? (
                    <span className="text-green-400 font-medium">Yes</span>
                  ) : d.isCompliant === false ? (
                    <span className="text-amber-400">No</span>
                  ) : (
                    <span className="text-gray-600">--</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {d.drmmMatch === true ? (
                    <span className="text-green-400 font-medium">True</span>
                  ) : d.drmmMatch === false ? (
                    <span className="text-gray-600">False</span>
                  ) : (
                    <span className="text-gray-600">--</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-400 whitespace-nowrap font-mono">
                  {formatShortTimestamp(d.firstSeen)}
                </td>
                <td className="px-3 py-2 text-gray-400 whitespace-nowrap font-mono">
                  {formatShortTimestamp(d.lastSeen)}
                </td>
                <td className="px-3 py-2 text-center text-green-400 font-mono">
                  {d.successCount || 0}
                </td>
                <td className="px-3 py-2 text-center font-mono">
                  {d.failCount > 0 ? (
                    <span className="text-red-400">{d.failCount}</span>
                  ) : (
                    <span className="text-gray-600">0</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center whitespace-nowrap">
                  {d.riskMin != null ? (
                    <span className={`font-mono ${d.riskMax >= 100 ? 'text-red-400' : d.riskMax >= 30 ? 'text-amber-400' : d.riskMin < 0 ? 'text-blue-400' : 'text-green-400'}`}>
                      {d.riskMin === d.riskMax ? d.riskMin : `${d.riskMin} \u2013 ${d.riskMax}`}
                    </span>
                  ) : (
                    <span className="text-gray-600">--</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Reusable sortable table header cell */
function ThSortable({ column, label, onSort, center, children }) {
  return (
    <th
      onClick={onSort ? () => onSort(column) : undefined}
      className={`text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 select-none whitespace-nowrap ${
        center ? 'text-center' : 'text-left'
      } ${onSort ? 'cursor-pointer hover:text-gray-300' : ''}`}
    >
      <span className={`inline-flex items-center ${center ? 'justify-center' : ''}`}>
        {label}
        {children}
      </span>
    </th>
  );
}
